/* The cashier's deep links, as the whitelabel reads them (QueryParamsProvider
   + WalletHubProvider + the deposit flow's useTokens/useNetworks):

     ?modal=balance|deposit|withdraw   opens the wallet on that tab
     ?depositMethod=crypto|card_payments|bank_transfer
                                       picks the deposit rail; the legacy
                                       transfer_crypto and buy_crypto still work
     ?depositToken=<symbol>            picks the currency on the crypto rail
     ?depositNetwork=<network id>      and its network

   The template's wallet has no separate balance tab — balances head both
   tabs — so `balance` opens Deposit, as the closest match. The address is kept
   in step with the wallet while it is open, so a link copied from the bar
   reopens what the player was looking at. */

export type WalletModalParam = "balance" | "deposit" | "withdraw";
export type DepositMethodParam = "crypto" | "card_payments" | "bank_transfer";

export type DepositPreset = {
  method?: DepositMethodParam;
  token?: string;
  network?: string;
};

export type WalletDeepLink = { modal?: WalletModalParam } & DepositPreset;

const MODALS: readonly string[] = ["balance", "deposit", "withdraw"];
const METHODS: readonly string[] = ["crypto", "card_payments", "bank_transfer"];
const LEGACY_METHODS: Record<string, DepositMethodParam> = {
  transfer_crypto: "crypto",
  buy_crypto: "card_payments",
};

export const isWalletModalParam = (value: string | null | undefined): value is WalletModalParam =>
  typeof value === "string" && MODALS.includes(value);

export const parseDepositMethod = (value: string | null | undefined): DepositMethodParam | undefined => {
  if (!value) return undefined;
  const mapped = LEGACY_METHODS[value] ?? value;
  return METHODS.includes(mapped) ? (mapped as DepositMethodParam) : undefined;
};

export const parseWalletDeepLink = (params: URLSearchParams): WalletDeepLink => {
  const modal = params.get("modal");

  return {
    modal: isWalletModalParam(modal) ? modal : undefined,
    method: parseDepositMethod(params.get("depositMethod")),
    token: params.get("depositToken") || undefined,
    network: params.get("depositNetwork") || undefined,
  };
};

export const walletTabFor = (modal: WalletModalParam): "Deposit" | "Withdraw" =>
  modal === "withdraw" ? "Withdraw" : "Deposit";

export const modalParamFor = (tab: "Deposit" | "Withdraw"): WalletModalParam =>
  tab === "Withdraw" ? "withdraw" : "deposit";

/* The template's rail keys, against the whitelabel's method names. */
export type Rail = "crypto" | "card" | "bank";

export const railFor = (method: DepositMethodParam): Rail =>
  method === "card_payments" ? "card" : method === "bank_transfer" ? "bank" : "crypto";

export const methodFor = (rail: Rail): DepositMethodParam =>
  rail === "card" ? "card_payments" : rail === "bank" ? "bank_transfer" : "crypto";

/* The crypto route a preset names: the exact currency and network when both
   match — on the remembered bankroll when two bankrolls take the same pair —
   the currency's first network when only it does, nothing otherwise (the
   caller keeps its own default). */
export const presetRoute = <
  T extends { bankroll: { id: string }; token: { symbol: string; network: { id: string } } },
>(
  options: T[],
  preset: { token?: string; network?: string; bankrollId?: string },
): T | undefined => {
  if (!preset.token) return undefined;

  const ofToken = options.filter((option) => option.token.symbol === preset.token);
  const onNetwork = ofToken.filter((option) => option.token.network.id === preset.network);

  return (
    onNetwork.find((option) => option.bankroll.id === preset.bankrollId) ?? onNetwork.at(0) ?? ofToken.at(0)
  );
};

/* The query string this module last wrote, so the deep-link reader can tell
   the wallet's own bookkeeping from a link the player followed — otherwise a
   wallet that closes itself (the email gate) would be reopened by the very
   `?modal=withdraw` it had just written. */
let ownQuery: string | null = null;

export const isOwnQuery = (query: string) => ownQuery !== null && query === ownQuery;

/* The whitelabel's updateSearchParams: set or drop each key on the current
   address, in place, without a navigation. */
export const updateSearchParams = (entries: Record<string, string | null | undefined>) => {
  if (typeof window === "undefined") return;

  const params = new URLSearchParams(window.location.search);

  for (const [key, value] of Object.entries(entries)) {
    if (value) params.set(key, value);
    else params.delete(key);
  }

  const query = params.toString();
  const next = query ? `${window.location.pathname}?${query}` : window.location.pathname;

  if (next === `${window.location.pathname}${window.location.search}`) return;

  ownQuery = query;
  window.history.replaceState(window.history.state, "", `${next}${window.location.hash}`);
};

/* The last deposit choice, remembered per browser like the whitelabel's
   depositMethod / depositToken / depositNetwork / depositBankrollId keys, so
   the wallet reopens where the player left it. A deep link wins over it. */
const REMEMBERED = "ns-wallet-deposit";

export type RememberedDeposit = DepositPreset & { bankrollId?: string };

export const readRememberedDeposit = (storage: Pick<Storage, "getItem"> | undefined): RememberedDeposit => {
  try {
    const raw = storage?.getItem(REMEMBERED);
    const value: unknown = raw ? JSON.parse(raw) : null;

    if (typeof value !== "object" || value === null) return {};

    const record = value as Record<string, unknown>;
    const text = (key: string) => (typeof record[key] === "string" ? (record[key] as string) : undefined);

    return {
      method: parseDepositMethod(text("method")),
      token: text("token"),
      network: text("network"),
      bankrollId: text("bankrollId"),
    };
  } catch {
    return {};
  }
};

export const writeRememberedDeposit = (storage: Pick<Storage, "setItem"> | undefined, value: RememberedDeposit) => {
  try {
    storage?.setItem(REMEMBERED, JSON.stringify(value));
  } catch {
    /* Private mode or a full quota: remembering is a convenience. */
  }
};

export const browserStorage = () => {
  try {
    return typeof window === "undefined" ? undefined : window.localStorage;
  } catch {
    return undefined;
  }
};
